// Import necessary React hooks and libraries
import React, { useState, useEffect } from 'react';
// Animation library for React
import { AnimatePresence } from 'framer-motion';
// Contentful client for fetching data
import client from './contentful';
// Main styles
import './App.css';
// Helmet for managing document head
import { HelmetProvider } from 'react-helmet-async';

// Import components
import Header from './components/component/Header';
import WorksPage from './components/pages/WorksPage';
import ProjectPage from './components/pages/ProjectPage';
import MessagesBoardPage from './components/pages/MessagesBoardPage';
import PageNotFound from './components/pages/PageNotFound';
// Routing components
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Loader from './components/component/Loader';
import ShowsPage from './components/pages/ShowsPage';
import ContactPage from './components/pages/ContactPage';
import MailingListPage from './components/pages/MailingListPage';
import Footer from './components/component/Footer';

// Main application component that handles routing and data fetching
function AppContent() {
    // Get current route location
    const location = useLocation();

    // State management
    const [pages, setPages] = useState([]);       // Stores works/projects data
    const [shows, setShows] = useState([]);       // Stores shows/events data
    const [contact, setContact] = useState([]);   // Stores contact information
    const [showLoader, setShowLoader] = useState(true);  // Controls initial loader visibility
    const [loading, setLoading] = useState(false);      // Controls loading state

    // Disable right-click context menu
    window.addEventListener(
        'contextmenu',
        function (e) {
            e.preventDefault();
        },
        false
    );

    // Strip query parameters from URL to prevent duplicate content
    useEffect(() => {
        if (location.search) {
            window.history.replaceState({}, '', location.pathname);
        }
    }, [location]);

    // Callback function when loader finishes
    const handleLoaderFinish = () => {
        setLoading(false);
    };

    // Effect to update document title based on current route
    useEffect(() => {
        // Format the page title based on the current path
        const path = location.pathname;
        const pageTitle = path === '/'
            ? 'nthng'  // Default title for home
            : path
                .replace('/', '') // Remove leading slash
                .replace(/-/g, ' ') // Convert URL-friendly dashes to spaces
                .split(' ') // Split into words
                .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Title case each word
                .join(' '); // Rejoin into a single string
        document.title = `${pageTitle}`; // Update the browser tab title
    }, [location]);


    // Fetch works/projects data from Contentful
    useEffect(() => {
        const fetchPages = async () => {
            try {
                // Fetch entries of type 'worksTemplatePage' from Contentful
                const response = await client.getEntries({
                    content_type: 'worksTemplatePage',
                    include: 2 // Include linked entries (nested content)
                });

                // Transform the response data into a more usable format
                const works = response.items.map(item => ({
                    id: item.sys.id,               // Unique identifier
                    title: item.fields.title,       // Work title
                    category: item.fields.category, // Work category
                    date: item.fields.date,         // Publication/creation date
                    url: item.fields.url,           // URL slug for routing
                    fields: item.fields,            // All original fields
                    mainPicture: item.fields.mainPicture, // Featured image
                    type: item.fields.type,         // Type of work
                    buyLink: item.fields.buyLink,   // Purchase link if applicable
                }));

                setPages(works); // Update state with fetched works

            } catch (error) {
                console.error('Error fetching pages:', error);
                // In a production app, you might want to set an error state here
            }
        };

        fetchPages(); // Execute the fetch
    }, []); // Empty dependency array means this runs once on mount

    // Fetch shows/events data from Contentful
    useEffect(() => {
        const fetchShows = async () => {
            try {
                // Fetch entries of type 'shows' from Contentful
                const response = await client.getEntries({
                    content_type: 'shows',
                });

                // Transform the shows data
                const shows = response.items.map(item => ({
                    id: item.sys.id,               // Unique identifier
                    title: item.fields.title,       // Show title
                    category: item.fields.category, // Show category/type
                    date: item.fields.date,         // Event date
                    link: item.fields.link,         // External link for more info
                    location: item.fields.location,  // Venue/location
                }));

                setShows(shows); // Update state with fetched shows

            } catch (error) {
                console.error('Error fetching shows:', error);
                // In a production app, you might want to set an error state here
            }
        };

        fetchShows(); // Execute the fetch
    }, []); // Empty dependency array means this runs once on mount


    // Fetch contact information from Contentful
    useEffect(() => {
        const fetchContact = async () => {
            try {
                // Fetch contact page content from Contentful
                const response = await client.getEntries({
                    content_type: 'contactPage',
                });

                // Extract contact information
                const contact = response.items.map(item => ({
                    id: item.sys.id,                     // Unique identifier
                    contactInfo: item.fields.contactInfo, // Contact details
                }));


                setContact(contact); // Update state with contact info

            } catch (error) {
                console.error('Error fetching contact information:', error);
                // In a production app, you might want to set an error state here
            }
        };

        fetchContact(); // Execute the fetch
    }, []); // Empty dependency array means this runs once on mount

    /*   if (loading) return <div>Loading...</div>; */

    // Main render method
    return (
        <>
            {/* Show loader if needed */}
            {showLoader && <Loader />}

            <div className="App">
                {/* Show loading indicator when content is being loaded */}
                {loading && <Loader onFinish={handleLoaderFinish} />}

                {/* Main header component */}
                <Header />

                {/* Main content area */}
                <main>
                    {/* Animation wrapper for page transitions */}
                    <AnimatePresence mode="wait">
                        {/* Application routes */}
                        <Routes location={location} key={location.pathname}>
                            {/* Redirect root to /works */}
                            {/*   <Route path="/" element={<Navigate to="/works" replace />} /> */}
                            <Route path="/" element={<WorksPage pages={pages} />} />
                            {/* Static routes */}
                            <Route path="/shows" element={<ShowsPage shows={shows} />} />
                            <Route path="/newsletter" element={<MailingListPage />} />
                            <Route path="/works" element={<WorksPage pages={pages} />} />
                            <Route path="/messages" element={<MessagesBoardPage />} />
                            <Route path="/contact" element={<ContactPage contact={contact} />} />
                            <Route path="/page-not-found" element={<PageNotFound />} />

                            {/* Dynamic routes for individual work pages */}
                            {pages.map(page => (
                                <Route
                                    key={page.id}
                                    path={`/${page.url}`}
                                    element={<ProjectPage page={page} />}
                                />
                            ))}
                            {/* Catch-all route for non-existent URLs */}
                            <Route path="*" element={<PageNotFound />} />
                            {/*     <Route path="*" element={<Navigate to="/page-not-found" />} /> */}

                            {/*   <Route path="*" element={<Navigate to="/" replace />} /> */}
                        </Routes>
                    </AnimatePresence>
                </main>

                {/* Footer component */}
                <Footer />
            </div>
        </>
    );
}


// Root application component that wraps everything with the Router
function App() {
    return (
        <HelmetProvider>
            <Router>
                <AppContent />
            </Router>
        </HelmetProvider>
    );
}

export default App;
